import React, { Component } from "react";
import { connect } from "react-redux";
import {
	useReactTable,
	getCoreRowModel,
	getSortedRowModel,
	getFilteredRowModel,
	flexRender,
} from "@tanstack/react-table";
import "./react-table-base.css";
import "./SortableTable.css";
import {
	setTableSort,
	setTableColumnWidths,
} from "../../actions/tableSortingActions";

import {
	goToPage,
	setFilteredCount,
} from "../../actions/globalPaginationActions";
import {
	IconAlertTriangle,
	IconArrowNarrowDown,
	IconArrowNarrowUp,
} from "@tabler/icons-react";
import { getColumnFilterValue } from "./tableFilterUtils";

export const DEFAULT_COLUMN_WIDTH = 150;
// Default width for a table's primary column (name, subject, etc.)
export const MAIN_COLUMN_WIDTH = DEFAULT_COLUMN_WIDTH * 3;
export const ID_COLUMN_WIDTH = 70;

function escapeRegex(string) {
	return string.replace(/[-/\\^$+?.()|[\]{}]/g, "\\$&");
}

export const nullsLastSort = (rowA, rowB, colId) => {
	const a = rowA.getValue(colId);
	const b = rowB.getValue(colId);
	if (a == null && b == null) return 0;
	if (a == null) return 1;
	if (b == null) return -1;
	const al = typeof a === "string" ? a.toLowerCase() : a;
	const bl = typeof b === "string" ? b.toLowerCase() : b;
	return al < bl ? -1 : al > bl ? 1 : 0;
};

// Functional wrapper component to use hooks
function TableWrapper(props) {
	const {
		data,
		columns,
		loading,
		defaultSorted,
		userOverriddenSort,
		onSortedChange,
		tableUniqueID,
		setTableSort,
		filterable,
		filtered,
		onFilteredChange,
		goToPage,
		setFilteredCount,
		filteringCompleteCallback,
		activeFilters,
		setTableColumnWidths,
		orderByMethod,
	} = props;

	// Convert react-table v6 columns to TanStack v8 format
	const tanstackColumns = React.useMemo(() => {
		return columns.map((col, index) => {
			const columnDef = {
				id: col.accessor || col.id || `column_${index}`,
				header: col.Header,
				size:
					col.width !== undefined ? col.width : DEFAULT_COLUMN_WIDTH,
				minSize: col.minWidth || 30,
				maxSize: col.maxWidth,
				enableSorting: col.sortable !== false,
				enableResizing: col.resizable !== false,
				enableColumnFilter: filterable && col.filterable !== false,
				filterFn: filterable
					? (row, columnId, filterValue) => {
							if (!filterValue) return true;
							const column = columns.find(
								(c) =>
									c.accessor === columnId || c.id === columnId
							);

							const value = getColumnFilterValue(
								column,
								row.original,
								row.getValue(columnId)
							);

							if (value == null) return false;
							const stringValue = String(value).toLowerCase();

							if (column?.wildcard) {
								const re = new RegExp(
									escapeRegex(filterValue).replace(
										/\*/g,
										".*"
									),
									"i"
								);
								return re.test(stringValue);
							}

							return stringValue.includes(
								filterValue.toLowerCase()
							);
						}
					: undefined,
				meta: {
					placeholder:
						col.Placeholder === ""
							? ""
							: col.Placeholder ||
								(col.width < 51 ? "" : "Filter..."),
				},
			};

			// Handle accessor - can be string or function.
			// Always use accessorFn for string accessors to convert null → undefined,
			// which lets sortUndefined: 'last' keep nulls at the bottom on all columns.
			columnDef.sortUndefined = "last";
			if (typeof col.accessor === "string") {
				const key = col.accessor;
				columnDef.accessorFn = (row) => row[key] ?? undefined;
			} else if (typeof col.accessor === "function") {
				columnDef.accessorFn = col.accessor;
			}

			if (col.sortingFn) {
				columnDef.sortingFn = col.sortingFn;
			} else if (col.filterValueMethod) {
				// Use filterValueMethod as the accessorFn value (undefined for null/empty)
				// so sortUndefined: 'last' handles null-last without being flipped by desc.
				// A custom sortingFn is not needed — default alphanumeric handles the rest.
				columnDef.accessorFn = (row) => {
					const val = col.filterValueMethod(row);
					return val != null ? val : undefined;
				};
			}

			// Handle cell rendering
			if (col.Cell) {
				// For filterValueMethod columns the accessorFn was overridden above to
				// return the formatted string value, so restore the raw accessor value
				// for Cell so it receives the original data type (e.g. array for tags).
				const cellUsesRawAccessor =
					col.filterValueMethod &&
					!col.sortingFn &&
					typeof col.accessor === "string";
				const rawKey = cellUsesRawAccessor ? col.accessor : null;
				columnDef.cell = (info) => {
					const v6Row = {
						original: info.row.original,
						value: rawKey
							? info.row.original[rawKey]
							: info.getValue(),
						row: info.row.original,
					};
					return col.Cell(v6Row);
				};
			} else if (columnDef.accessorKey || columnDef.accessorFn) {
				columnDef.cell = (info) => {
					const value = info.getValue();
					return value != null ? String(value) : "";
				};
			}

			return columnDef;
		});
	}, [columns, filterable]);

	// Set up initial sorting state from persisted Redux/localStorage or defaultSorted
	const columnIds = React.useMemo(
		() => new Set(tanstackColumns.map((col) => col.id)),
		[tanstackColumns]
	);
	const initialSorting = React.useMemo(() => {
		const sortConfig = userOverriddenSort?.sort || defaultSorted || [];
		return sortConfig.map((sort) => ({
			id: sort.id,
			desc: sort.desc || false,
		}));
	}, [userOverriddenSort, defaultSorted]);

	// Initialize column sizes from persisted Redux state
	const initialColumnSizing = React.useMemo(() => {
		const saved = userOverriddenSort?.resizedColumns;
		if (!saved || !saved.length) return {};
		const sizing = {};
		saved.forEach((col) => {
			sizing[col.id] = col.value;
		});
		return sizing;
	}, []);

	const [sorting, setSorting] = React.useState(initialSorting);
	const [columnSizing, setColumnSizing] = React.useState(initialColumnSizing);
	const [columnFilters, setColumnFilters] = React.useState([]);

	// When tableUniqueID changes (e.g. DocumentList tab switch), reset sorting
	// to the persisted state for the new table. useState only uses its initial
	// value on mount, so we need this effect for prop-driven identity changes.
	const prevTableIdRef = React.useRef(tableUniqueID);
	React.useEffect(() => {
		if (prevTableIdRef.current !== tableUniqueID) {
			setSorting(initialSorting);
			prevTableIdRef.current = tableUniqueID;
		}
	}, [tableUniqueID, initialSorting]);

	// Filter out sort entries for columns that no longer exist (e.g. hidden via visibility toggle).
	// Must be synchronous (useMemo) because TanStack reads sorting during render — a useEffect
	// would fire too late and the error would already be logged.
	const effectiveSorting = React.useMemo(
		() => sorting.filter((sort) => columnIds.has(sort.id)),
		[sorting, columnIds]
	);

	// Clear column filters when parent clears the controlled `filtered` prop
	const prevFilteredRef = React.useRef(filtered);
	React.useEffect(() => {
		if (
			prevFilteredRef.current?.length > 0 &&
			(!filtered || filtered.length === 0)
		) {
			setColumnFilters([]);
		}
		prevFilteredRef.current = filtered;
	}, [filtered]);

	// Reset local column sizing when Redux state is explicitly cleared (e.g. "Reset Column Widths")
	const savedResizedColumns = userOverriddenSort?.resizedColumns;
	const prevSavedRef = React.useRef(savedResizedColumns);
	React.useEffect(() => {
		if (
			prevSavedRef.current?.length > 0 &&
			savedResizedColumns?.length === 0
		) {
			setColumnSizing({});
		}
		prevSavedRef.current = savedResizedColumns;
	}, [savedResizedColumns]);

	// Persist column sizes to Redux/localStorage when they change
	const isInitialMount = React.useRef(true);
	React.useEffect(() => {
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}
		if (setTableColumnWidths && tableUniqueID) {
			const resizedColumns = Object.entries(columnSizing).map(
				([id, value]) => ({ id, value })
			);
			setTableColumnWidths(tableUniqueID, resizedColumns);
		}
	}, [columnSizing, setTableColumnWidths, tableUniqueID]);

	const table = useReactTable({
		data: data,
		columns: tanstackColumns,
		state: {
			sorting: effectiveSorting,
			columnSizing,
			columnFilters,
		},
		onSortingChange: (updater) => {
			const newSorting =
				typeof updater === "function"
					? updater(effectiveSorting)
					: updater;
			setSorting(newSorting);

			// Convert back to react-table v6 format for redux
			const v6Format = newSorting.map((s) => ({
				id: s.id,
				desc: s.desc,
			}));
			setTableSort(tableUniqueID, v6Format, false);

			if (onSortedChange) {
				onSortedChange(v6Format);
			}
		},
		onColumnFiltersChange: (updater) => {
			const newFilters =
				typeof updater === "function"
					? updater(columnFilters)
					: updater;
			setColumnFilters(newFilters);

			if (filterable && onFilteredChange) {
				const v6Format = newFilters.map((f) => ({
					id: f.id,
					value: f.value,
				}));
				onFilteredChange(v6Format);
			}

			if (filterable && goToPage) {
				goToPage(1);
				const tbody = document.querySelector(".rt-tbody");
				if (tbody) tbody.scrollTop = 0;
			}

			if (filterable && setFilteredCount) {
				if (newFilters.length === 0) {
					setFilteredCount(-1);
				} else {
					// Count updated via useEffect after re-render
				}
			}
		},
		onColumnSizingChange: setColumnSizing,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		enableSorting: true,
		enableSortingRemoval: false,
		enableColumnResizing: true,
		columnResizeMode: "onChange",
		enableFilters: filterable || false,
	});

	// Reset filtered count when all column filters are cleared.
	// This is intentionally separate from the effect below so that a data reference
	// change (e.g. parent re-render) does not dispatch setFilteredCount(-1), which
	// would reset currentPage to 1 via the pagination reducer.
	React.useEffect(() => {
		if (!filterable || !setFilteredCount) return;
		if (columnFilters.length === 0) {
			setFilteredCount(-1);
		}
	}, [columnFilters]);

	// Recalculate filtered count and invoke callback when active filters change or data changes.
	React.useEffect(() => {
		if (!filterable || !setFilteredCount) return;
		if (columnFilters.length === 0) return;
		const filteredRows = table.getFilteredRowModel().rows;
		setFilteredCount(filteredRows.length);

		if (filteringCompleteCallback) {
			filteringCompleteCallback(
				filteredRows.map((r) => r.original),
				columnFilters[columnFilters.length - 1]?.id,
				activeFilters
			);
		}
	}, [columnFilters, data]);

	// Build CSS variables for column sizes
	const tableRef = React.useRef(null);

	// Columns use a fixed pixel width, never flex-grow — a flex-grow column
	// with unbounded text would balloon `.rt-tr`'s min-width: max-content
	// (needed for horizontal scroll) instead of truncating.
	const [containerWidth, setContainerWidth] = React.useState(0);
	React.useEffect(() => {
		const el = tableRef.current;
		if (!el || typeof ResizeObserver === "undefined") return;
		const observer = new ResizeObserver((entries) => {
			setContainerWidth(entries[0].contentRect.width);
		});
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	// One `--col-<id>-size` CSS var per column, applied as each cell's inline
	// width — dragging a resize handle only needs to update these vars, not
	// re-render every cell. The last column ignores its own size and instead
	// gets max(nominal, leftover space) so it fills the table instead of
	// leaving a gap after it.
	const columnSizeVars = React.useMemo(() => {
		const vars = {};
		const headers = table.getFlatHeaders();
		const lastHeader = headers[headers.length - 1];
		let sumOfOtherColumns = 0;
		headers.forEach((header) => {
			if (header === lastHeader) return;
			const size = header.getSize();
			vars[`--col-${header.id}-size`] = size;
			sumOfOtherColumns += size;
		});
		if (lastHeader) {
			const nominalSize = lastHeader.column.columnDef.size;
			vars[`--col-${lastHeader.id}-size`] = Math.max(
				nominalSize,
				containerWidth - sumOfOtherColumns
			);
		}
		return vars;
	}, [table.getState().columnSizing, tanstackColumns, containerWidth]);

	// Custom resize handler: pure DOM manipulation during drag, React update only on release
	const handleResizeStart = React.useCallback(
		(header, e) => {
			e.preventDefault();
			const startX = e.clientX ?? e.touches[0].clientX;
			const startWidth = header.getSize();
			const colId = header.id;
			const minSize = header.column.columnDef.minSize || 30;

			const onMove = (ev) => {
				const clientX = ev.clientX ?? ev.touches[0].clientX;
				const newWidth = Math.max(
					minSize,
					startWidth + clientX - startX
				);
				if (tableRef.current) {
					tableRef.current.style.setProperty(
						`--col-${colId}-size`,
						newWidth
					);
				}
			};

			const onEnd = (ev) => {
				document.removeEventListener("mousemove", onMove);
				document.removeEventListener("mouseup", onEnd);
				document.removeEventListener("touchmove", onMove);
				document.removeEventListener("touchend", onEnd);
				const clientX =
					ev.clientX ?? ev.changedTouches?.[0]?.clientX ?? startX;
				const newWidth = Math.max(
					minSize,
					startWidth + clientX - startX
				);
				setColumnSizing((prev) => ({ ...prev, [colId]: newWidth }));
			};

			document.addEventListener("mousemove", onMove);
			document.addEventListener("mouseup", onEnd);
			document.addEventListener("touchmove", onMove);
			document.addEventListener("touchend", onEnd);
		},
		[setTableColumnWidths, tableUniqueID]
	);

	// Calculate pagination info
	const PaginationComponent = props.PaginationComponent;
	const rawRows = table.getRowModel().rows;

	// Apply orderByMethod (e.g. foldersOnTop) as a post-sort step
	const allRows = React.useMemo(() => {
		if (!orderByMethod) return rawRows;
		// Build v6-compatible wrappers with _original for orderByMethod
		const wrapped = rawRows.map((row, idx) => ({
			...row,
			_original: row.original,
			_index: idx,
		}));
		// When no sort columns are active, provide a no-op comparator
		// so orderByMethod can still apply its own logic (e.g. foldersOnTop)
		const funcs = sorting.length
			? sorting.map((s) => {
					const colDef = columns.find(
						(c) =>
							(typeof c.accessor === "string"
								? c.accessor
								: c.id) === s.id
					);
					// Use filterValueMethod for sort value when present (and no explicit
					// sortingFn), so empty collections produce null and sort last.
					const getVal =
						colDef?.filterValueMethod && !colDef?.sortingFn
							? (row) => colDef.filterValueMethod(row._original)
							: (row) => row._original[s.id];
					return (a, b) => {
						const va = getVal(a);
						const vb = getVal(b);
						if (va == null && vb == null) return 0;
						// Pre-flip null result based on sort direction so that
						// defaultOrderBy's desc-flip cancels out, keeping nulls last.
						if (va == null) return s.desc ? -1 : 1;
						if (vb == null) return s.desc ? 1 : -1;
						const sa =
							typeof va === "string" ? va.toLowerCase() : va;
						const sb =
							typeof vb === "string" ? vb.toLowerCase() : vb;
						if (sa > sb) return 1;
						if (sa < sb) return -1;
						return 0;
					};
				})
			: [() => 0];
		const dirs = sorting.length
			? sorting.map((s) => (s.desc ? "desc" : "asc"))
			: ["asc"];
		return orderByMethod(wrapped, funcs, dirs, "_index");
	}, [rawRows, orderByMethod, sorting, columns]);
	const totalRows = allRows.length;
	const pageSize = props.defaultPageSize || 50;
	const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));

	// Local page state for uncontrolled pagination
	const [localPage, setLocalPage] = React.useState(1);
	const currentPage = props.uncontrolledPagination
		? localPage
		: props.currentPage || 1;

	const handlePageChange = React.useCallback(
		(page) => {
			if (props.uncontrolledPagination) {
				setLocalPage(page + 1);
			}
		},
		[props.uncontrolledPagination]
	);

	// Slice rows for current page when pagination is active
	const paginatedRows = PaginationComponent
		? allRows.slice((currentPage - 1) * pageSize, currentPage * pageSize)
		: allRows;

	return (
		<div
			className={
				"SortableTable -striped -highlight " + (props.className || "")
			}
		>
			{/* the last refresh was rate limited, so these rows may be out of date */}
			{props.dataIsStale && !loading && (
				<div className="SortableTable-staleData">
					<IconAlertTriangle size={18} />
					Couldn&apos;t refresh: too many requests. This may be out of
					date; please wait a moment, then refresh the page.
				</div>
			)}
			<div className="rt-table" ref={tableRef} style={columnSizeVars}>
				{loading && (
					<div className="rt-loading -active">
						<div className="-loading-inner"></div>
					</div>
				)}
				<div className="rt-thead">
					{table.getHeaderGroups().map((headerGroup) => (
						<div key={headerGroup.id} className="rt-tr">
							{headerGroup.headers.map((header, index) => {
								const isLastVisible =
									index === headerGroup.headers.length - 1;
								const canResize =
									header.column.getCanResize() &&
									!isLastVisible;
								return (
									<div
										key={header.id}
										className="rt-th leftAlignedTableHeader"
										style={{
											width: `calc(var(--col-${header.id}-size) * 1px)`,
											position: "relative",
										}}
									>
										<div
											style={{
												display: "flex",
												alignItems: "center",
											}}
											onClick={header.column.getToggleSortingHandler()}
										>
											<span>
												{header.isPlaceholder
													? null
													: flexRender(
															header.column
																.columnDef
																.header,
															header.getContext()
														)}
											</span>
											{header.column.getIsSorted() ? (
												<span
													style={{
														marginLeft: "4px",
														display: "flex",
													}}
												>
													{header.column.getIsSorted() ===
													"desc" ? (
														<IconArrowNarrowDown
															size={16}
														/>
													) : (
														<IconArrowNarrowUp
															size={16}
														/>
													)}
												</span>
											) : (
												<span style={{ width: 1 }} />
											)}
										</div>
										{canResize && (
											<div
												onMouseDown={(e) =>
													handleResizeStart(header, e)
												}
												onTouchStart={(e) =>
													handleResizeStart(header, e)
												}
												className="rt-resizer"
												style={{
													position: "absolute",
													right: 0,
													top: 0,
													height: "100%",
													width: "10px",
													cursor: "col-resize",
													userSelect: "none",
													touchAction: "none",
												}}
											/>
										)}
									</div>
								);
							})}
						</div>
					))}
					{filterable && (
						<div className="rt-tr rt-tr-filters">
							{table.getFlatHeaders().map((header) => (
								<div
									key={header.id}
									className="rt-th"
									style={{
										width: `calc(var(--col-${header.id}-size) * 1px)`,
									}}
								>
									{header.column.getCanFilter() ? (
										<input
											type="text"
											value={
												table
													.getState()
													.columnFilters.find(
														(f) =>
															f.id === header.id
													)?.value ?? ""
											}
											onChange={(e) =>
												header.column.setFilterValue(
													e.target.value
												)
											}
											placeholder={
												header.column.columnDef.meta
													?.placeholder
											}
											className="rt-filter-input"
										/>
									) : null}
								</div>
							))}
						</div>
					)}
				</div>
				<div className="rt-tbody">
					{paginatedRows.length === 0 && !loading ? (
						<div className="rt-noData">
							{/* stale data may be empty only because it couldn't load */}
							{!props.dataIsStale && (
								<h3>{props.noResultsText}</h3>
							)}
						</div>
					) : (
						paginatedRows.map((row) => (
							<div key={row.id} className="rt-tr-group">
								<div className="rt-tr">
									{row.getVisibleCells().map((cell) => (
										<div
											key={cell.id}
											className={`rt-td${cell.column.id === "selected" ? " rt-td-checkbox" : ""}`}
											style={{
												width: `calc(var(--col-${cell.column.id}-size) * 1px)`,
											}}
										>
											<div className="rt-td-content">
												{flexRender(
													cell.column.columnDef.cell,
													cell.getContext()
												)}
											</div>
										</div>
									))}
								</div>
							</div>
						))
					)}
				</div>
			</div>
			{PaginationComponent && (
				<PaginationComponent
					data={props.data || []}
					rowCount={totalRows}
					pages={totalPages}
					page={currentPage - 1}
					pageSize={pageSize}
					onPageChange={handlePageChange}
				/>
			)}
		</div>
	);
}

class UnconnectedSortableTable extends Component {
	constructor(props) {
		super(props);

		this.mixinColumnVisibility = this.mixinColumnVisibility.bind(this);
	}
	mixinColumnVisibility() {
		return this.props.columns.map((item) => {
			var newColumnConfig = {
				...item,
				headerClassName: "leftAlignedTableHeader",
			};

			if (this.props.columnVisibility) {
				newColumnConfig.show =
					item.accessor === "selected" ||
					this.props.columnVisibility[item.accessor] !== false;
			}
			return newColumnConfig;
		});
	}
	render() {
		const columnsWithVisibility = this.mixinColumnVisibility();

		// Filter out columns where show is false
		const visibleColumns = columnsWithVisibility.filter(
			(col) => col.show !== false
		);

		return <TableWrapper {...this.props} columns={visibleColumns} />;
	}
}

const SortableTable = connect(
	(state, ownProps) => ({
		scrollbarWidth: state.global.scrollbarWidth,
		userOverriddenSort: state.tableSorting[ownProps.tableUniqueID],
		currentPage: state.pagination.currentPage,
	}),
	{ setTableSort, setTableColumnWidths }
)(UnconnectedSortableTable);

class UnconnectedFilterableTable extends Component {
	componentWillUnmount() {
		this.props.setFilteredCount(-1);
	}
	render() {
		return (
			<SortableTable
				{...this.props}
				filterable={true}
				onFilteredChange={this.props.onFilteredChange}
				filteringCompleteCallback={this.props.filteringCompleteCallback}
				activeFilters={this.props.activeFilters}
			/>
		);
	}
}
const FilterableTable = connect(null, { goToPage, setFilteredCount })(
	UnconnectedFilterableTable
);

export default SortableTable;
export { FilterableTable };
